import { useEffect, useState } from 'react'
import type { FinancialDoc } from './types'
import { listFinancialsByDeal, listFinancialsByProject } from '../../api'
import { LABELS } from './labels'

type Source =
  | { kind: 'deal'; dealId: number | string }
  | { kind: 'project'; subProjectId: number | string }

const TYPE_LABEL: Record<string, string> = {
  ar_invoice: 'AR Invoice', customer_payment: 'Payment In',
  ap_bill: 'AP Bill', ap_scan: 'AP Scan', bill_payment: 'Payment Out',
  payment: 'Payment', receipt: 'Receipt', other: 'Other',
  estimate: 'Estimate', credit_memo: 'Credit Memo',
  statement: 'Statement', sales_receipt: 'Sales Receipt',
}

function fmtMoney(n: number | null | undefined): string {
  return n == null ? '--' : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`
}

function fmtDate(s: string | null): string {
  if (!s) return '--'
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s)
  return m ? `${m[2]}/${m[3]}/${m[1]}` : s
}

export function FinancialsCards({ source }: { source: Source }) {
  const [items, setItems] = useState<FinancialDoc[]>([])
  const [warning, setWarning] = useState<string | null>(null)
  // Local preview-modal state — keeps FinancialsCards self-contained without
  // having to thread a setter down from App.tsx.
  const [preview, setPreview] = useState<{ url: string; title: string } | null>(null)

  useEffect(() => {
    if (!preview) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setPreview(null) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [preview])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const r = source.kind === 'deal'
          ? await listFinancialsByDeal(Number(source.dealId))
          : await listFinancialsByProject(source.subProjectId, { limit: 500 })
        if (!cancelled) setItems(r.items ?? [])
      } catch (e: any) {
        if (!cancelled) setWarning(String(e?.message ?? e))
      }
    })()
    return () => { cancelled = true }
  }, [source.kind, (source as any).dealId, (source as any).subProjectId])

  const txnTypes = new Set(['ar_invoice','customer_payment','ap_bill','ap_scan','bill_payment'])
  const docs = items.filter(d => !txnTypes.has(d.doc_type))

  // Pair each AR Invoice with its matching Payment (same counterparty + same
  // amount, payment dated on/after the invoice) and present one row per
  // invoice lifecycle: Invoice / Invoice date / Paid date / Amount / Balance.
  // Same for AP Bills paired with their Bill Payments. Unmatched payments
  // (refunds, advance payments, overpayments) get their own rows so the
  // numbers still reconcile.
  type Lifecycle = {
    key: string
    kind: 'ar' | 'ap' | 'payment_only_in' | 'payment_only_out'
    invoiceDate: string | null
    paidDate: string | null
    counterparty: string | null
    amount: number
    balance: number | null
    invoiceUrl: string | null
    paymentUrl: string | null
    split?: boolean
    splitCount?: number
    splitChildKeys?: string[]
    isSplitChild?: boolean
    stemRole?: 'top' | 'mid' | 'bottom'
  }

  const arInvoices = items.filter(d => d.doc_type === 'ar_invoice')
  const customerPayments = items.filter(d => d.doc_type === 'customer_payment')
  const apBills = items.filter(d => d.doc_type === 'ap_bill' || d.doc_type === 'ap_scan')
  const billPayments = items.filter(d => d.doc_type === 'bill_payment')

  const lifecycles: Lifecycle[] = []
  const usedPaymentKeys = new Set<string>()
  const matchPayment = (
    pool: FinancialDoc[],
    invDate: string | null,
    counterparty: string | null,
    amount: number,
  ): FinancialDoc | null => {
    let best: FinancialDoc | null = null
    let bestDelta = Infinity
    const invMs = invDate ? new Date(invDate).getTime() : null
    for (const p of pool) {
      const ckey = `${p.source}:${p.id}`
      if (usedPaymentKeys.has(ckey)) continue
      if (Math.abs(Number(p.amount ?? 0) - amount) > 0.01) continue
      if ((p.counterparty || '') !== (counterparty || '')) continue
      // Allow payments on the same day or after the invoice; cap the window
      // at one year so a coincidence years later doesn't get auto-paired.
      const pMs = p.date ? new Date(p.date).getTime() : null
      if (invMs != null && pMs != null) {
        if (pMs < invMs - 24 * 3600 * 1000) continue
        const delta = pMs - invMs
        if (delta > 365 * 24 * 3600 * 1000) continue
        if (delta < bestDelta) { best = p; bestDelta = delta }
      } else if (best == null) {
        best = p
      }
    }
    if (best) usedPaymentKeys.add(`${best.source}:${best.id}`)
    return best
  }

  // Split-payment matcher: when no single payment equals the invoice, find a
  // set of 2+ still-unused payments (same counterparty, on/after the invoice,
  // within a year) that SUM to the invoice amount. Only used for invoices the
  // ledger already shows as paid (balance ~ 0), so a coincidental sum on a
  // genuinely open invoice can't trigger it. Bounded subset-sum (<=14 cands).
  const findSplitPayments = (
    pool: FinancialDoc[],
    invDate: string | null,
    counterparty: string | null,
    amount: number,
    excludeKeys: Set<string>,
  ): FinancialDoc[] | null => {
    if (!(amount > 0.01)) return null
    const invMs = invDate ? new Date(invDate).getTime() : null
    const cands = pool.filter(p => {
      const ckey = `${p.source}:${p.id}`
      if (usedPaymentKeys.has(ckey) || excludeKeys.has(ckey)) return false
      if ((p.counterparty || '') !== (counterparty || '')) return false
      const amt = Number(p.amount ?? 0)
      if (!(amt > 0.01) || amt > amount + 0.01) return false
      const pMs = p.date ? new Date(p.date).getTime() : null
      if (invMs != null && pMs != null) {
        if (pMs < invMs - 24 * 3600 * 1000) return false
        if (pMs - invMs > 365 * 24 * 3600 * 1000) return false
      }
      return true
    })
    if (cands.length < 2) return null
    const arr = cands
      .slice()
      .sort((a, b) => (a.date || '').localeCompare(b.date || ''))
      .slice(0, 14)
    let best: FinancialDoc[] | null = null
    for (let mask = 1; mask < (1 << arr.length); mask++) {
      let sum = 0, cnt = 0
      for (let i = 0; i < arr.length; i++) {
        if (mask & (1 << i)) { sum += Number(arr[i].amount ?? 0); cnt++ }
      }
      if (cnt >= 2 && Math.abs(sum - amount) <= 0.01) {
        if (!best || cnt < best.length) best = arr.filter((_, i) => (mask & (1 << i)) !== 0)
      }
    }
    return best
  }

  for (const inv of arInvoices) {
    const pay = matchPayment(customerPayments, inv.date, inv.counterparty, Number(inv.amount ?? 0))
    lifecycles.push({
      key: `ar:${inv.source}:${inv.id}`,
      kind: 'ar',
      invoiceDate: inv.date,
      paidDate: pay?.date ?? null,
      counterparty: inv.counterparty,
      amount: Number(inv.amount ?? 0),
      balance: inv.balance,
      invoiceUrl: inv.document_url,
      paymentUrl: pay?.document_url ?? null,
    })
  }
  for (const bill of apBills) {
    const pay = matchPayment(billPayments, bill.date, bill.counterparty, Number(bill.amount ?? 0))
    lifecycles.push({
      key: `ap:${bill.source}:${bill.id}`,
      kind: 'ap',
      invoiceDate: bill.date,
      paidDate: pay?.date ?? null,
      counterparty: bill.counterparty,
      amount: Number(bill.amount ?? 0),
      balance: bill.balance,
      invoiceUrl: bill.document_url,
      paymentUrl: pay?.document_url ?? null,
    })
  }

  // Second pass — split payments. For any invoice/bill still showing "Unpaid"
  // but whose ledger balance is ~0 (so it IS paid), look for 2+ payments that
  // together sum to the amount and mark it paid (paid date = the last payment)
  // + split. Payments are NOT consumed, so each still shows as its own row;
  // splitUsedKeys only stops one payment paying down two different invoices.
  const splitUsedKeys = new Set<string>()
  for (const lc of lifecycles) {
    if (lc.paidDate != null) continue
    if (lc.balance == null || Number(lc.balance) > 0.01) continue
    if (!(lc.amount > 0.01)) continue
    const pool = lc.kind === 'ar' ? customerPayments : lc.kind === 'ap' ? billPayments : null
    if (!pool) continue
    const subset = findSplitPayments(pool, lc.invoiceDate, lc.counterparty, lc.amount, splitUsedKeys)
    if (subset && subset.length >= 2) {
      const latest = subset.reduce<FinancialDoc | null>((m, p) => {
        const t = p.date ? new Date(p.date).getTime() : 0
        const mt = m && m.date ? new Date(m.date).getTime() : -1
        return t > mt ? p : m
      }, null)
      const childPrefix = lc.kind === 'ar' ? 'pi' : 'po'
      lc.paidDate = latest?.date ?? lc.paidDate
      lc.split = true
      lc.splitCount = subset.length
      lc.splitChildKeys = subset.map(p => `${childPrefix}:${p.source}:${p.id}`)
      for (const p of subset) splitUsedKeys.add(`${p.source}:${p.id}`)
    }
  }

  for (const p of customerPayments) {
    const k = `${p.source}:${p.id}`
    if (usedPaymentKeys.has(k)) continue
    lifecycles.push({
      key: `pi:${k}`, kind: 'payment_only_in',
      invoiceDate: null, paidDate: p.date,
      counterparty: p.counterparty, amount: Number(p.amount ?? 0),
      balance: p.balance, invoiceUrl: null, paymentUrl: p.document_url,
    })
  }
  for (const p of billPayments) {
    const k = `${p.source}:${p.id}`
    if (usedPaymentKeys.has(k)) continue
    lifecycles.push({
      key: `po:${k}`, kind: 'payment_only_out',
      invoiceDate: null, paidDate: p.date,
      counterparty: p.counterparty, amount: Number(p.amount ?? 0),
      balance: p.balance, invoiceUrl: null, paymentUrl: p.document_url,
    })
  }
  // Mark split component payments so they render grouped under their parent
  // invoice (indented) instead of floating as separate rows.
  const splitChildParent = new Map<string, string>()
  for (const lc of lifecycles) {
    if (lc.split && lc.splitChildKeys) {
      for (const ck of lc.splitChildKeys) splitChildParent.set(ck, lc.key)
    }
  }
  for (const lc of lifecycles) {
    if (splitChildParent.has(lc.key)) lc.isSplitChild = true
  }

  lifecycles.sort((a, b) => {
    const aDate = a.invoiceDate || a.paidDate || ''
    const bDate = b.invoiceDate || b.paidDate || ''
    return bDate.localeCompare(aDate)
  })

  // Re-thread each split group so its payments sit ABOVE the invoice in
  // chronological (newest-first) order, with the invoice as the foot of the
  // stem. The group is anchored where its newest payment falls in the sorted
  // list, and a continuous left stem links the payments down into the invoice.
  const lcByKey = new Map(lifecycles.map(l => [l.key, l]))
  const orderedLifecycles: Lifecycle[] = []
  const emittedKeys = new Set<string>()
  const emitGroup = (parent: Lifecycle) => {
    const kids = (parent.splitChildKeys || [])
      .map(ck => lcByKey.get(ck))
      .filter((c): c is Lifecycle => !!c)
      .sort((a, b) => (b.paidDate || '').localeCompare(a.paidDate || ''))
    kids.forEach((child, i) => {
      child.stemRole = i === 0 ? 'top' : 'mid'
      if (!emittedKeys.has(child.key)) { orderedLifecycles.push(child); emittedKeys.add(child.key) }
    })
    parent.stemRole = 'bottom'
    if (!emittedKeys.has(parent.key)) { orderedLifecycles.push(parent); emittedKeys.add(parent.key) }
  }
  for (const lc of lifecycles) {
    if (emittedKeys.has(lc.key)) continue
    if (lc.isSplitChild) {
      const parent = lcByKey.get(splitChildParent.get(lc.key) || '')
      if (parent) { emitGroup(parent); continue }
    }
    if (lc.split && lc.splitChildKeys) { emitGroup(lc); continue }
    orderedLifecycles.push(lc); emittedKeys.add(lc.key)
  }
  for (const lc of lifecycles) {
    if (!emittedKeys.has(lc.key)) { orderedLifecycles.push(lc); emittedKeys.add(lc.key) }
  }

  const invoiced = items
    .filter(d => d.doc_type === 'ar_invoice')
    .reduce((s, d) => s + (d.amount ?? 0), 0)
  const paid = items
    .filter(d => d.doc_type === 'customer_payment')
    .reduce((s, d) => s + (d.amount ?? 0), 0)
  const balance = invoiced - paid
  const apTotal = items
    .filter(d => d.doc_type === 'ap_bill' || d.doc_type === 'ap_scan' || d.source === 'acct_bill_payment')
    .reduce((s, d) => s + (d.amount ?? 0), 0)

  const projectIds = new Set(items.filter(d => d.sub_project_id != null).map(d => d.sub_project_id))

  return (
    <>
      {warning && (
        <div className="crp-card" style={{ borderColor: '#fecaca' }}>
          <div className="crp-card-body" style={{ color: '#991b1b', fontSize: '0.85rem' }}>{warning}</div>
        </div>
      )}

      <div className="crp-card">
        <div className="crp-card-header"><h3>Financial summary</h3></div>
        <div className="crp-card-body">
          <div className="crp-stats-row">
            <div className="crp-stat"><span className="crp-stat-val">{fmtMoney(invoiced)}</span><span className="crp-stat-label">Invoiced</span></div>
            <div className="crp-stat"><span className="crp-stat-val">{fmtMoney(paid)}</span><span className="crp-stat-label">Paid</span></div>
            <div className="crp-stat"><span className="crp-stat-val" style={{ color: balance > 0 ? '#dc2626' : '#16a34a' }}>{fmtMoney(balance)}</span><span className="crp-stat-label">Balance</span></div>
            <div className="crp-stat"><span className="crp-stat-val">{fmtMoney(apTotal)}</span><span className="crp-stat-label">AP Total</span></div>
          </div>
        </div>
      </div>

      <div className="crp-card">
        <div className="crp-card-header"><h3>Invoices &amp; Payments ({lifecycles.length})</h3></div>
        <div className="crp-card-body" style={{ padding: 0 }}>
          {lifecycles.length === 0 ? (
            <div className="crp-empty-card">No invoices or payments yet.</div>
          ) : (
            <table className="crp-mini-table">
              <thead><tr>
                <th>Type</th><th>Counterparty</th><th>Invoice date</th><th>Paid date</th>
                <th style={{ textAlign: 'right' }}>Amount</th>
                <th style={{ textAlign: 'right' }}>Balance</th>
              </tr></thead>
              <tbody>
                {orderedLifecycles.map(lc => {
                  const url = lc.invoiceUrl || lc.paymentUrl
                  const label =
                    lc.kind === 'ar' ? 'AR Invoice' :
                    lc.kind === 'ap' ? 'AP Bill' :
                    lc.kind === 'payment_only_in' ? 'Payment In' : 'Payment Out'
                  const title = `${label} — ${lc.counterparty ?? lc.invoiceDate ?? lc.paidDate ?? ''}`
                  return (
                    <tr
                      key={lc.key}
                      onClick={url ? () => setPreview({ url, title }) : undefined}
                      style={{ cursor: url ? 'pointer' : 'default' }}
                      title={url ? 'View PDF' : undefined}
                    >
                      <td style={lc.stemRole ? { position: 'relative', paddingLeft: 30, color: lc.isSplitChild ? '#64748b' : undefined } : undefined}>
                        {lc.stemRole && (
                          <>
                            <span style={{ position: 'absolute', left: 14, top: lc.stemRole === 'top' ? '50%' : 0, bottom: lc.stemRole === 'bottom' ? '50%' : 0, borderLeft: '1px solid #cbd5e1' }} />
                            <span style={{ position: 'absolute', left: 14, top: '50%', width: 10, borderTop: '1px solid #cbd5e1' }} />
                          </>
                        )}
                        {label}
                      </td>
                      <td>{lc.counterparty ?? '--'}</td>
                      <td>{fmtDate(lc.invoiceDate)}</td>
                      <td style={{ color: lc.paidDate ? '#16a34a' : '#dc2626' }}>
                        {lc.paidDate ? fmtDate(lc.paidDate) : 'Unpaid'}
                        {lc.split && <span style={{ marginLeft: 6, padding: '1px 6px', borderRadius: 9999, fontSize: '0.62rem', fontWeight: 700, background: '#e0e7ff', color: '#4338ca', verticalAlign: 'middle' }}>Split ×{lc.splitCount ?? 2}</span>}
                      </td>
                      <td style={{ textAlign: 'right' }}>{fmtMoney(lc.amount)}</td>
                      <td style={{ textAlign: 'right' }}>{fmtMoney(lc.balance)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="crp-card">
        <div className="crp-card-header"><h3>Documents ({docs.length})</h3></div>
        <div className="crp-card-body" style={{ padding: 0 }}>
          {docs.length === 0 ? (
            <div className="crp-empty-card">No receipts or uploaded documents.</div>
          ) : (
            <table className="crp-mini-table">
              <thead><tr><th>Type</th><th>Counterparty</th><th>Date</th><th style={{ textAlign: 'right' }}>Amount</th></tr></thead>
              <tbody>
                {docs.map(r => {
                  const url = r.document_url as string | null | undefined
                  const title = `${TYPE_LABEL[r.doc_type] ?? r.doc_type} — ${r.counterparty ?? r.date ?? ''}`
                  return (
                    <tr
                      key={`${r.source}:${r.id}`}
                      onClick={url ? () => setPreview({ url, title }) : undefined}
                      style={{ cursor: url ? 'pointer' : 'default' }}
                      title={url ? 'View PDF' : undefined}
                    >
                      <td>{TYPE_LABEL[r.doc_type] ?? r.doc_type}</td>
                      <td>{r.counterparty ?? '--'}</td>
                      <td>{fmtDate(r.date)}</td>
                      <td style={{ textAlign: 'right' }}>{fmtMoney(r.amount)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {preview && (
        <div className="pdf-preview-overlay" onClick={() => setPreview(null)}>
          <div className="pdf-preview-modal" onClick={e => e.stopPropagation()}>
            <div className="pdf-preview-header">
              <h3 className="pdf-preview-title">{preview.title}</h3>
              <button className="pdf-preview-close" onClick={() => setPreview(null)} aria-label="Close preview">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <iframe className="pdf-preview-iframe" src={preview.url} title={preview.title} />
          </div>
        </div>
      )}

      {projectIds.size > 1 && (
        <div className="crp-card">
          <div className="crp-card-header"><h3>{LABELS.projects} ({projectIds.size})</h3></div>
          <div className="crp-card-body" style={{ padding: 0 }}>
            <table className="crp-mini-table">
              <thead><tr><th>{LABELS.project} ID</th><th style={{ textAlign: 'right' }}>Docs</th><th style={{ textAlign: 'right' }}>Total</th></tr></thead>
              <tbody>
                {[...projectIds].map(pid => {
                  const projItems = items.filter(d => d.sub_project_id === pid)
                  const total = projItems.reduce((s, d) => s + (d.amount ?? 0), 0)
                  return (
                    <tr key={String(pid)}>
                      <td>#{pid}</td>
                      <td style={{ textAlign: 'right' }}>{projItems.length}</td>
                      <td style={{ textAlign: 'right' }}>{fmtMoney(total)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  )
}
